/**
 * home-timeline.js
 * Custom element for the Home Timeline section.
 * Desktop: Year bar navigation + continuous-scroll carousel + month filtering
 * Mobile:  Hero Swiper slider
 */

class HomeTimeline extends BaseComponent {
  init() {
    this.desktopEl = this.querySelector('.home-timeline__desktop');
    this.mobileEl = this.querySelector('.home-timeline__mobile');
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;

    // Read settings from data attributes
    this.autoScroll = this.dataset.autoScroll === 'true';
    this.scrollSpeed = parseInt(this.dataset.scrollSpeed, 10) || 5;
    this.carouselHeight = parseInt(this.dataset.carouselHeight, 10) || 500;
    this.adaptiveHeight = this.dataset.adaptiveHeight === 'true';

    // State
    this.desktopSwipers = {};
    this.heroSwiper = null;
    this.activeYearIndex = 0;
    this.observers = [];
    this.uiObserver = null;

    // Init based on viewport
    if (this.isMobile) {
      this.initMobile();
    } else {
      this.initDesktop();
    }

    // Listen for resize
    this._onResize = this._handleResize.bind(this);
    window.addEventListener('resize', this._onResize);

    // Start ResizeObserver for robust height tracking
    this.initUIObserver();

    // Ensure metrics are calculated after header/footer variables have settled
    requestAnimationFrame(() => this.updateUIMetrics());
  }

  initUIObserver() {
    if (this.uiObserver) this.uiObserver.disconnect();

    this.uiObserver = new ResizeObserver(() => {
      this.updateUIMetrics();
    });

    // Observe global and navigation elements
    this.uiObserver.observe(document.documentElement); // Handles window resize & font changes
    const bar = this.querySelector('.timeline-bar');
    const labels = this.querySelector('.timeline-bar__labels');
    if (bar) this.uiObserver.observe(bar);
    if (labels) this.uiObserver.observe(labels);

    // Observe Header and Footer directly since they provide the height variables we depend on
    const header = document.querySelector('header-root');
    const footer = document.querySelector('footer-root');
    if (header) this.uiObserver.observe(header);
    if (footer) this.uiObserver.observe(footer);

    // Initial observe for current months bar if it exists
    this.updateMonthsObservation();
  }

  updateMonthsObservation() {
    if (!this.uiObserver) return;
    const activeMonths = this.querySelector('.timeline-panel.is-active .timeline-months');
    if (activeMonths) {
      this.uiObserver.observe(activeMonths);
    }
  }

  updateUIMetrics() {
    if (this.isMobile || !this.isConnected) return;

    const bar = this.querySelector('.timeline-bar');
    const labels = this.querySelector('.timeline-bar__labels');
    const activePanel = this.querySelector('.timeline-panel.is-active');
    const activeMonths = activePanel ? activePanel.querySelector('.timeline-months') : null;

    if (bar) {
      this.style.setProperty('--timeline-bar-height', `${bar.offsetHeight}px`);
    }
    if (labels) {
      this.style.setProperty('--timeline-labels-height', `${labels.offsetHeight}px`);
    }
    if (activeMonths) {
      this.style.setProperty('--timeline-months-height', `${activeMonths.offsetHeight}px`);
    } else {
      this.style.setProperty('--timeline-months-height', '0px');
    }

    // Full Available Height Calculation in JS for reliability
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    const footerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--footer-height'), 10) || 0;
    const barHeight = bar ? bar.offsetHeight : 0;
    const labelsHeight = labels ? labels.offsetHeight : 0;
    const monthsHeight = activeMonths ? activeMonths.offsetHeight : 0;

    // Use a 2px safety buffer to prevent decimal rounding from triggering browser scrollbars
    const buffer = 2;
    const availableHeight = window.innerHeight - headerHeight - footerHeight - barHeight - labelsHeight - monthsHeight - buffer;
    this.style.setProperty('--timeline-available-height', `${availableHeight}px`);

    // Ensure the active carousel keeps its height variables even if Swiper.destroy() wiped them
    if (activePanel) {
      const swiperEl = activePanel.querySelector('.timeline-carousel');
      if (swiperEl) {
        if (this.adaptiveHeight) {
          swiperEl.style.setProperty('--card-height', 'var(--timeline-available-height)');
          swiperEl.style.height = 'var(--timeline-available-height)';
        } else {
          swiperEl.style.setProperty('--card-height', `${this.carouselHeight}px`);
          swiperEl.style.height = `${this.carouselHeight}px`;
        }
      }
    }

    // Force Swiper update after height change to fix "vague" slide height bugs
    if (activePanel) {
      const idx = parseInt(activePanel.dataset.yearIndex, 10);
      if (this.desktopSwipers[idx]) {
        this.desktopSwipers[idx].update();
      }
    }
  }

  /* ═══════════════════════════════════════════
     DESKTOP
     ═══════════════════════════════════════════ */
  initDesktop() {
    this.yearButtons = this.desktopEl.querySelectorAll('.dot-pagination__dot.timeline-bar__year[data-year-index]');
    this.yearLabels = this.desktopEl.querySelectorAll('.timeline-bar__labels .timeline-bar__label[data-year-index]');
    this.panels = this.desktopEl.querySelectorAll('.timeline-panel');
    this.prevArrow = this.desktopEl.querySelector('.dot-pagination__arrow--prev');
    this.nextArrow = this.desktopEl.querySelector('.dot-pagination__arrow--next');

    // Year click
    this.yearButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.yearIndex, 10);
        this.activateYear(idx);
      });
    });

    // Arrow navigation with looping
    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', () => {
        const newIndex = (this.activeYearIndex - 1 + this.yearButtons.length) % this.yearButtons.length;
        this.activateYear(newIndex);
      });
    }
    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', () => {
        const newIndex = (this.activeYearIndex + 1) % this.yearButtons.length;
        this.activateYear(newIndex);
      });
    }

    // Store original slide HTML for each panel (for month filtering rebuild)
    this.panelSlideData = {};
    this.panels.forEach((panel) => {
      const idx = parseInt(panel.dataset.yearIndex, 10);
      const slides = panel.querySelectorAll('.swiper-slide');
      this.panelSlideData[idx] = [];
      slides.forEach((slide) => {
        this.panelSlideData[idx].push({
          month: slide.dataset.month || 'all',
          html: slide.outerHTML,
        });
      });
    });

    // Detect initial active year from DOM (Liquid-rendered)
    const initialActiveBtn = Array.from(this.yearButtons).find(btn => btn.classList.contains('is-active'));
    const initialIndex = initialActiveBtn ? parseInt(initialActiveBtn.dataset.yearIndex, 10) : 0;

    this.activeYearIndex = initialIndex;

    // Filter by the active month tag for the initial year
    const initialPanel = this.panels[initialIndex];
    if (initialPanel) {
      const initialMonthTag = initialPanel.querySelector('.timeline-months__tag.is-active');
      if (initialMonthTag) {
        this.filterByMonth(initialMonthTag);
      } else {
        this.initPanelSwiper(initialIndex);
      }
    }

    this.updateArrows();

    // Month tags
    this.desktopEl.querySelectorAll('.timeline-months__tag').forEach((tag) => {
      tag.addEventListener('click', () => {
        this.filterByMonth(tag);
      });
    });

    // Text effects
    this.initTextEffects();
  }

  activateYear(index) {
    this.activeYearIndex = index;

    // Update year dot buttons
    this.yearButtons.forEach((btn) => {
      btn.classList.toggle('is-active', parseInt(btn.dataset.yearIndex, 10) === index);
    });

    // Update year labels
    if (this.yearLabels) {
      this.yearLabels.forEach((label) => {
        label.classList.toggle('is-active', parseInt(label.dataset.yearIndex, 10) === index);
      });
    }

    // Update panels
    let activePanel = null;
    this.panels.forEach((panel) => {
      const isActive = parseInt(panel.dataset.yearIndex, 10) === index;
      panel.classList.toggle('is-active', isActive);
      if (isActive) activePanel = panel;
    });

    // Init swiper for this panel if not already (filterByMonth handles this)
    if (activePanel) {
      const activeMonthTag = activePanel.querySelector('.timeline-months__tag.is-active');
      if (activeMonthTag) {
        this.filterByMonth(activeMonthTag);
      } else {
        this.initPanelSwiper(index);
      }
    }

    this.updateArrows();

    // Re-trigger text effects for newly visible panel
    this.initTextEffects();

    // Update metrics and observers for the new active panel AFTER swiper init (to restore wiped styles)
    this.updateMonthsObservation();
    this.updateUIMetrics();
  }

  updateArrows() {
    // Arrows are always enabled for looping
    if (this.prevArrow) this.prevArrow.disabled = false;
    if (this.nextArrow) this.nextArrow.disabled = false;
  }

  initPanelSwiper(index) {
    // Destroy existing if present (could be re-init after filter)
    if (this.desktopSwipers[index]) {
      this.desktopSwipers[index].destroy(true, true);
      delete this.desktopSwipers[index];
    }

    const panel = this.panels[index];
    if (!panel) return;

    const swiperEl = panel.querySelector('.timeline-carousel');
    if (!swiperEl) return;

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    if (slides.length === 0) return;

    // Map scroll speed 1-10 to actual speed (10000ms slow → 1000ms fast)
    const speedMs = Math.round(10000 - (this.scrollSpeed - 1) * 1000);

    const swiperConfig = {
      slidesPerView: 'auto',
      spaceBetween: 12,
      centeredSlides: false,
      centeredSlidesBounds: true,
      initialSlide: 0,
      resistance: true,
      resistanceRatio: 0,
      freeMode: {
        enabled: true,
        momentum: true,
        momentumRatio: 0.6,
        momentumBounce: false,

      },
      grabCursor: true,
      loop: false,
      speed: speedMs,
      centerInsufficientSlides: true,
      mousewheel: {
        forceToAxis: true,
      },
    };

    if (this.autoScroll && slides.length > 1) {
      swiperConfig.autoplay = {
        delay: 0,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    this.desktopSwipers[index] = new Swiper(swiperEl, swiperConfig);

    // Video visibility handling
    this.initVideoObservers(panel);
  }

  filterByMonth(tagEl) {
    const panel = tagEl.closest('.timeline-panel');
    if (!panel) return;

    const monthLabel = tagEl.dataset.month;
    const yearIndex = parseInt(panel.dataset.yearIndex, 10);

    // Update active tag
    panel.querySelectorAll('.timeline-months__tag').forEach((t) => {
      t.classList.toggle('is-active', t === tagEl);
    });

    // Rebuild slides: filter the stored slide data by month, then replace innerHTML
    const allSlides = this.panelSlideData[yearIndex] || [];
    const filtered = allSlides.filter(
      (s) => s.month === monthLabel || s.month === 'all'
    );

    const swiperEl = panel.querySelector('.timeline-carousel');
    if (!swiperEl) return;

    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    // Destroy existing Swiper first
    if (this.desktopSwipers[yearIndex]) {
      this.desktopSwipers[yearIndex].destroy(true, true);
      delete this.desktopSwipers[yearIndex];
    }

    // Replace slide HTML
    wrapper.innerHTML = filtered.map((s) => s.html).join('');

    // Re-init Swiper
    this.initPanelSwiper(yearIndex);

    // RESTORE HEIGHT METRICS: Swiper.destroy() wiped the heights, so re-apply them now
    this.updateUIMetrics();
  }

  /* ═══════════════════════════════════════════
     TEXT EFFECTS
     ═══════════════════════════════════════════ */
  initTextEffects() {
    const activePanel = this.desktopEl.querySelector('.timeline-panel.is-active');
    if (!activePanel) return;

    const textEls = activePanel.querySelectorAll('.media-card__text[data-text-effect]');
    textEls.forEach((el) => {
      const inner = el.querySelector('.media-card__text-inner');
      if (!inner) return;

      const effect = el.dataset.textEffect;

      // Create observer for viewport entry
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.playTextEffect(inner, effect);
              observer.disconnect();
            }
          });
        },
        { threshold: 0.3 }
      );

      observer.observe(el);
      this.observers.push(observer);
    });
  }

  playTextEffect(inner, effect) {
    switch (effect) {
      case 'Typewriter':
        this.typewriterEffect(inner);
        break;
      case 'Fade in':
      case 'Line reveal':
        // CSS-driven — just toggle the class
        requestAnimationFrame(() => {
          inner.classList.add('is-revealed');
        });
        break;
      default:
        inner.classList.add('is-revealed');
    }
  }

  typewriterEffect(inner) {
    const originalHTML = inner.innerHTML;
    inner.classList.add('is-revealed');

    // ── Collect all text nodes in DOM order ──
    const textNodes = [];
    const walker = document.createTreeWalker(inner, NodeFilter.SHOW_TEXT, null, false);
    let node;
    while ((node = walker.nextNode())) {
      textNodes.push({ node, original: node.textContent });
    }

    // Build a flat character map: [{nodeIndex, charIndex}]
    const charMap = [];
    textNodes.forEach((entry, nodeIdx) => {
      for (let i = 0; i < entry.original.length; i++) {
        charMap.push({ nodeIdx, charIdx: i });
      }
    });

    const totalChars = charMap.length;
    if (totalChars === 0) return;

    // Empty all text nodes (HTML tags stay in the DOM)
    textNodes.forEach((entry) => {
      entry.node.textContent = '';
    });

    const speed = 30; // ms per char

    // ── Reveal function: show first N characters across all nodes ──
    const revealUpTo = (count) => {
      // Reset all nodes
      textNodes.forEach((entry) => {
        entry.node.textContent = '';
      });

      // Fill characters up to `count`
      for (let i = 0; i < count; i++) {
        const { nodeIdx, charIdx } = charMap[i];
        textNodes[nodeIdx].node.textContent += textNodes[nodeIdx].original[charIdx];
      }
    };

    if (typeof gsap !== 'undefined') {
      const proxy = { chars: 0 };
      gsap.to(proxy, {
        chars: totalChars,
        duration: totalChars * (speed / 1000),
        ease: 'none',
        onUpdate() {
          revealUpTo(Math.floor(proxy.chars));
        },
        onComplete() {
          // Restore full original HTML for absolute fidelity
          inner.innerHTML = originalHTML;
          inner.classList.add('is-revealed');
        },
      });
    } else {
      // Fallback: plain JS interval
      let revealed = 0;
      const intervalId = setInterval(() => {
        if (revealed < totalChars) {
          revealed++;
          revealUpTo(revealed);
        } else {
          clearInterval(intervalId);
          inner.innerHTML = originalHTML;
          inner.classList.add('is-revealed');
        }
      }, speed);
    }
  }

  /* ═══════════════════════════════════════════
     VIDEO OBSERVERS
     ═══════════════════════════════════════════ */
  initVideoObservers(container) {
    const videos = container.querySelectorAll('.media-card__video');
    videos.forEach((video) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              video.play().catch(() => { });
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.3 }
      );
      observer.observe(video);
      this.observers.push(observer);
    });
  }

  /* ═══════════════════════════════════════════
     MOBILE
     ═══════════════════════════════════════════ */
  initMobile() {
    const heroEl = this.mobileEl.querySelector('.hero-slider');
    if (!heroEl) return;

    const slides = heroEl.querySelectorAll('.swiper-slide');
    if (slides.length === 0) return;

    this.heroSwiper = new Swiper(heroEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: slides.length > 1,
      pagination: {
        el: '.hero-slider__pagination',
        clickable: true,
      },
      autoplay: slides.length > 1
        ? { delay: 4000, disableOnInteraction: false }
        : false,
    });

    // Video observers for mobile
    this.initVideoObservers(this.mobileEl);
  }

  /* ═══════════════════════════════════════════
     RESIZE HANDLER
     ═══════════════════════════════════════════ */
  _handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;

    if (wasMobile !== this.isMobile) {
      // Viewport mode changed — re-init
      this.destroyAll();
      if (this.isMobile) {
        this.initMobile();
      } else {
        this.initDesktop();
      }
      this.updateUIMetrics();
    } else if (!this.isMobile) {
      // Just a normal resize, still need to check metrics
      this.updateUIMetrics();
    }
  }

  /* ═══════════════════════════════════════════
     CLEANUP
     ═══════════════════════════════════════════ */
  destroyAll() {
    // Desktop swipers
    Object.values(this.desktopSwipers).forEach((s) => s.destroy(true, true));
    this.desktopSwipers = {};

    // Mobile swiper
    if (this.heroSwiper) {
      this.heroSwiper.destroy(true, true);
      this.heroSwiper = null;
    }

    // Observers
    this.observers.forEach((obs) => obs.disconnect());
    this.observers = [];

    if (this.uiObserver) {
      this.uiObserver.disconnect();
      this.uiObserver = null;
    }
  }

  disconnectedCallback() {
    this.destroyAll();
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
  }
}

customElements.define('home-timeline', HomeTimeline);
